import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import FilterBar, { FilterSummary } from '../components/FilterBar';
import DisclaimerFooter from '../components/DisclaimerFooter';
import { urbanForestryData, CARP_PATH, OSRP_PATH } from '../data/urbanForestryData';
// Visual styling is shared with the CARP page so the two read as a family;
// UrbanForestry.css holds only this page's additions (CARP/OSRP overlap badges).
import './Carp2026.css';
import './UrbanForestry.css';
import { usePersistentFlag, usePersistentSet } from '../utils/expandedState';

// An objective counts as "in CARP" if it, or any of its actions, maps to CARP.
function objectiveInCarp(objective) {
  return Boolean(objective.partOfCarp) || (objective.actions || []).some(a => a.partOfCarp);
}

// Likewise for the Open Space & Recreation Plan (OSRP).
function objectiveInOsrp(objective) {
  return Boolean(objective.partOfOsrp) || (objective.actions || []).some(a => a.partOfOsrp);
}

function getAllObjectives(data) {
  const objectives = [];
  data.categories.forEach(cat => {
    cat.goals.forEach(goal => {
      goal.objectives.forEach(obj => objectives.push(obj));
    });
  });
  return objectives;
}

function UrbanForestry() {
  const [sel, setSel] = useState({ carp: new Set(), osrp: new Set() });
  const [expandedCategories, setExpandedCategories] = usePersistentSet('urban-forestry:expandedCategories', () => new Set(urbanForestryData.categories.map(c => c.id)));
  const [expandedGoals, setExpandedGoals] = usePersistentSet('urban-forestry:expandedGoals');
  const [expandedObjectives, setExpandedObjectives] = usePersistentSet('urban-forestry:expandedObjectives');
  const [purposeExpanded, setPurposeExpanded] = usePersistentFlag('urban-forestry:purposeExpanded');
  const [carpOverlapExpanded, setCarpOverlapExpanded] = usePersistentFlag('urban-forestry:carpOverlapExpanded');
  const [abbrExpanded, setAbbrExpanded] = usePersistentFlag('urban-forestry:abbrExpanded');

  useEffect(() => {
    document.title = 'Urban Forestry Climate Resilience Master Plan 2021';
  }, []);

  const allObjectives = useMemo(() => getAllObjectives(urbanForestryData), []);

  const dimensions = useMemo(() => {
    const carpCount = allObjectives.filter(objectiveInCarp).length;
    const osrpCount = allObjectives.filter(objectiveInOsrp).length;
    return [
      { key: 'carp', label: 'CARP overlap', values: [
        { value: 'carp', label: 'Part of CARP', count: carpCount },
        { value: 'noncarp', label: 'Not in CARP', count: allObjectives.length - carpCount },
      ] },
      { key: 'osrp', label: 'OSRP overlap', values: [
        { value: 'osrp', label: 'OSRP', count: osrpCount },
        { value: 'noosrp', label: 'Not in OSRP', count: allObjectives.length - osrpCount },
      ] },
    ];
  }, [allObjectives]);

  const toggleValue = (key, value) => setSel(prev => {
    const next = new Set(prev[key]);
    if (next.has(value)) next.delete(value); else next.add(value);
    return { ...prev, [key]: next };
  });
  const clearDimension = (key) => setSel(prev => ({ ...prev, [key]: new Set() }));
  const clearAllFilters = () => setSel({ carp: new Set(), osrp: new Set() });

  const matchesObjective = useMemo(() => {
    return (objective) => {
      if (sel.carp.size) {
        const inCarp = objectiveInCarp(objective);
        const ok = (sel.carp.has('carp') && inCarp) || (sel.carp.has('noncarp') && !inCarp);
        if (!ok) return false;
      }
      if (sel.osrp.size) {
        const inOsrp = objectiveInOsrp(objective);
        const ok = (sel.osrp.has('osrp') && inOsrp) || (sel.osrp.has('noosrp') && !inOsrp);
        if (!ok) return false;
      }
      return true;
    };
  }, [sel]);

  const filteredCategories = useMemo(() => {
    return urbanForestryData.categories.map(category => ({
      ...category,
      goals: category.goals.map(goal => ({
        ...goal,
        objectives: goal.objectives.filter(matchesObjective)
      })).filter(goal => goal.objectives.length > 0)
    })).filter(category => category.goals.length > 0);
  }, [matchesObjective]);

  const totalFiltered = useMemo(() =>
    filteredCategories.reduce((sum, cat) =>
      sum + cat.goals.reduce((s2, g) => s2 + g.objectives.length, 0), 0),
    [filteredCategories]);


  const toggle = (setter) => (id) => {
    setter(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleCategory = toggle(setExpandedCategories);
  const toggleGoal = toggle(setExpandedGoals);
  const toggleObjective = toggle(setExpandedObjectives);

  const expandAll = () => {
    setExpandedCategories(new Set(urbanForestryData.categories.map(c => c.id)));
    const goals = new Set();
    const objectives = new Set();
    urbanForestryData.categories.forEach(cat => {
      cat.goals.forEach(goal => {
        goals.add(goal.id);
        goal.objectives.forEach(o => objectives.add(o.id));
      });
    });
    setExpandedGoals(goals);
    setExpandedObjectives(objectives);
    setPurposeExpanded(true);
    setCarpOverlapExpanded(true);
    setAbbrExpanded(true);
  };

  const collapseAll = () => {
    setExpandedCategories(new Set());
    setExpandedGoals(new Set());
    setExpandedObjectives(new Set());
    setPurposeExpanded(false);
    setCarpOverlapExpanded(false);
    setAbbrExpanded(false);
  };

  const CarpBadge = ({ carpRef, className = '' }) => (
    <span className={`uf-carp-badge ${className}`}>
      Part of CARP{carpRef ? <span className="uf-carp-ref">{carpRef}</span> : null}
    </span>
  );

  const OsrpBadge = ({ osrpRef, className = '' }) => (
    <span className={`uf-osrp-badge ${className}`}>
      OSRP{osrpRef ? <span className="uf-osrp-ref">{osrpRef}</span> : null}
    </span>
  );

  const renderActions = (actions) => (
    <div className="carp-actions">
      {actions.map(action => (
        <div key={action.id} className="carp-action">
          <span className="carp-action-id">{action.id}.</span>
          <span className="carp-action-desc">
            {action.description}
            {(action.responsible || action.years) && (
              <span className="uf-action-meta">
                {action.responsible}
                {action.responsible && action.years ? ' · ' : ''}
                {action.years}
              </span>
            )}
            {action.partOfCarp && <CarpBadge carpRef={action.carpRef} className="uf-carp-badge-inline" />}
            {action.partOfOsrp && <OsrpBadge osrpRef={action.osrpRef} className="uf-carp-badge-inline" />}
          </span>
        </div>
      ))}
    </div>
  );

  const renderPreamble = (title, expanded, setExpanded, paragraphs) => (
    <div className="carp-preamble">
      <button
        className={`carp-preamble-header ${expanded ? 'expanded' : ''}`}
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className={`carp-chevron${expanded ? ' expanded' : ''}`} aria-hidden="true"></span>
        <span className="carp-preamble-title">{title}</span>
      </button>
      {expanded && (
        <div className="carp-preamble-body">
          {paragraphs.map((para, i) => <p key={i}>{para}</p>)}
        </div>
      )}
    </div>
  );

  return (
    <div className="carp-page">
      <a href="#main-content" className="carp-skip-link">Skip to main content</a>
      <Header
        title="Urban Forestry Climate Resilience Master Plan 2021"
        subtitle="Goals, Objectives & Actions"
        creditExtra="website design by Michael Rubenstein"
      />

      <main id="main-content" tabIndex={-1} className="carp-content">
        <div className="carp-intro">
          <p className="carp-updated">Adopted {urbanForestryData.adoptedDate}</p>
        </div>

        <div className="carp-controls">
          <FilterBar
            allCount={allObjectives.length}
            dimensions={dimensions}
            selected={sel}
            onToggleValue={toggleValue}
            onClearDimension={clearDimension}
            onClearAll={clearAllFilters}
            trailing={
              <>
                <button className="carp-expand-btn" onClick={expandAll}>Expand All</button>
                <button className="carp-expand-btn" onClick={collapseAll}>Collapse All</button>
              </>
            }
          />
        </div>

        <FilterSummary
          count={totalFiltered}
          noun="objective"
          nounPlural="objectives"
          dimensions={dimensions}
          selected={sel}
          onClearAll={clearAllFilters}
        />

        <div className="carp-preamble-sections">
          {renderPreamble('Purpose', purposeExpanded, setPurposeExpanded, urbanForestryData.introduction.purpose)}
          {renderPreamble('Relationship to the CARP', carpOverlapExpanded, setCarpOverlapExpanded, urbanForestryData.introduction.carpOverlap)}
          {renderPreamble('Abbreviations', abbrExpanded, setAbbrExpanded, urbanForestryData.introduction.abbreviations)}
        </div>

        <div className="carp-categories">
          {filteredCategories.map(category => (
            <div key={category.id} className="carp-category">
              <h2 className="carp-heading"><button
                className={`carp-category-header ${expandedCategories.has(category.id) ? 'expanded' : ''}`}
                onClick={() => toggleCategory(category.id)}
                aria-expanded={expandedCategories.has(category.id)}
              >
                <span className="carp-cathead-focus">
                  <span className={`carp-chevron carp-chevron-light${expandedCategories.has(category.id) ? ' expanded' : ''}`} aria-hidden="true"></span>
                  <div className="carp-category-header-text">
                    <span className="carp-category-name">{category.id}. {category.name}</span>
                  </div>
                </span>
              </button></h2>

              {expandedCategories.has(category.id) && (
                <div className="carp-category-body">
                  {category.goals.map(goal => (
                    <div key={goal.id} className="carp-goal">
                      <h3 className="carp-heading"><button
                        className={`carp-goal-header ${expandedGoals.has(goal.id) ? 'expanded' : ''}`}
                        onClick={() => toggleGoal(goal.id)}
                        aria-expanded={expandedGoals.has(goal.id)}
                      >
                        <span className={`carp-chevron${expandedGoals.has(goal.id) ? ' expanded' : ''}`} aria-hidden="true"></span>
                        <span className="carp-goal-id">{goal.id}</span>
                        <span className="carp-goal-title">
                          <span className="uf-goal-section">{goal.sectionLetter}. {goal.sectionName}</span>
                          {goal.title}
                        </span>
                      </button></h3>

                      {expandedGoals.has(goal.id) && (
                        <div className="carp-goal-body">
                          {goal.objectives.map(objective => {
                            const isExpanded = expandedObjectives.has(objective.id);
                            return (
                              <div key={objective.id} className="carp-strategy">
                                <h4 className="carp-heading"><button
                                  className={`carp-strategy-header ${isExpanded ? 'expanded' : ''}`}
                                  onClick={() => toggleObjective(objective.id)}
                                  aria-expanded={isExpanded}
                                >
                                  <span className={`carp-chevron${isExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
                                  <span className="carp-strategy-id">{objective.id}.</span>
                                  <span className="carp-strategy-title">{objective.title}</span>
                                  <span className="uf-obj-header-meta">
                                    {objective.partOfCarp && <CarpBadge carpRef={objective.carpRef} />}
                                    {objective.partOfOsrp && <OsrpBadge osrpRef={objective.osrpRef} />}
                                    <span className="carp-action-count">
                                      {objective.actions.length} action{objective.actions.length !== 1 ? 's' : ''}
                                    </span>
                                  </span>
                                </button></h4>

                                {isExpanded && (
                                  <div className="carp-strategy-body">
                                    {(objective.partOfCarp || objective.partOfOsrp) && (
                                      <div className="uf-objective-meta">
                                        {objective.partOfCarp && (
                                          <span className="uf-meta-item">
                                            <span className="uf-meta-label">Part of CARP:</span> {objective.carpRef}{' '}
                                            <Link to={CARP_PATH} className="uf-carp-link">(view CARP)</Link>
                                          </span>
                                        )}
                                        {objective.partOfOsrp && (
                                          <span className="uf-meta-item">
                                            <span className="uf-meta-label">OSRP:</span> {objective.osrpRef}{' '}
                                            <Link to={OSRP_PATH} className="uf-carp-link">(view OSRP)</Link>
                                          </span>
                                        )}
                                      </div>
                                    )}
                                    {objective.actions && objective.actions.length > 0 && renderActions(objective.actions)}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <DisclaimerFooter>
          <p>
            For source information from the Town of Brookline, go to the{' '}
            <a href="https://www.brooklinema.gov/1758/Urban-Forest-Climate-Resiliency-Master-P" target="_blank" rel="noopener noreferrer">
              Urban Forest Climate Resiliency Master Plan<span className="visually-hidden"> (opens in new tab)</span>
            </a>
            {' '}web page on the Town of Brookline website.
          </p>
        </DisclaimerFooter>
      </main>
    </div>
  );
}

export default UrbanForestry;
